import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  HardHat, ArrowLeft, BookOpen, Search,
  Home, Wrench, Thermometer, Droplet, Zap, Paintbrush,
  TreePine, Layers, Sun
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  roofing: Home,
  remodeling: Wrench,
  hvac: Thermometer,
  plumbing: Droplet,
  electrical: Zap,
  painting: Paintbrush,
  flooring: Layers,
  landscaping: TreePine,
  windows: Layers,
  concrete: Layers,
  solar: Sun,
};

const TipsPage = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('es') ? 'es' : 'en';

  const [categories, setCategories] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catsRes, tipsRes] = await Promise.all([
        axios.get(`${API}/tips/categories`),
        axios.get(`${API}/tips/`, { params: { language } }),
      ]);
      setCategories(catsRes.data);
      setTips(tipsRes.data);
    } catch (e) {
      console.error('Failed to load tips', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredTips = tips.filter((tip) => {
    const matchesCat = !selectedCategory || tip.category === selectedCategory;
    const q = query.trim().toLowerCase();
    const matchesQuery =
      !q ||
      tip.title?.toLowerCase().includes(q) ||
      tip.introduction?.toLowerCase().includes(q);
    return matchesCat && matchesQuery;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Construction Tips & Guides — Expert Home Improvement Advice"
        description="Free AI-powered construction tips and home-improvement guides from ContractorsLab. Roofing, plumbing, electrical, HVAC, remodeling and more — in English and Spanish."
        keywords="ContractorsLab tips, construction tips, home improvement guides, DIY vs pro, roofing tips, plumbing tips, HVAC advice, contractor advice"
        path="/tips"
      />
      {/* Nav */}
      <nav className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="tips-home-link">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">
                CONTRACTORSLAB
              </span>
            </Link>

            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link
                to="/"
                className="text-slate-300 hover:text-white font-medium text-sm flex items-center gap-1"
                data-testid="back-home-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b-4 border-orange-600">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-orange-400 font-bold uppercase tracking-wider text-sm">
              AI-Powered Guides
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[0.95]" data-testid="tips-title">
            {t('tips.title')}
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mt-4 max-w-2xl">
            {t('tips.subtitle')}
          </p>
        </div>
      </section>

      {/* Tips list */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {/* Search */}
          <div className="mb-6 max-w-xl">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:border-orange-600 focus:outline-none"
                data-testid="tips-search-input"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm border transition-colors ${
                !selectedCategory
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
              }`}
              data-testid="filter-all-categories"
            >
              {t('tips.allCategories')}
            </button>
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.slug] || Wrench;
              const active = selectedCategory === cat.slug;
              return (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm border transition-colors flex items-center gap-1.5 ${
                    active
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                  }`}
                  data-testid={`filter-category-${cat.slug}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat[`name_${language}`] || cat.name_en}
                </button>
              );
            })}
          </div>

          {/* Tips Grid */}
          {loading ? (
            <div className="text-center py-20 text-slate-500">{t('common.loading')}</div>
          ) : filteredTips.length === 0 ? (
            <div className="text-center py-20 text-slate-500" data-testid="no-tips-message">
              {t('tips.noResults')}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tips-grid">
              {filteredTips.map((tip) => {
                const Icon = CATEGORY_ICONS[tip.category] || Wrench;
                return (
                  <Link
                    key={tip.id}
                    to={`/tips/${tip.slug || tip.id}`}
                    className="group text-left bg-white border border-slate-200 hover:border-orange-600 hover:shadow-lg transition-all p-6 rounded-sm block"
                    data-testid={`tip-card-${tip.slug || tip.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 bg-orange-100 group-hover:bg-orange-600 transition-colors rounded-sm flex items-center justify-center">
                        <Icon className="w-4 h-4 text-orange-600 group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-xs uppercase font-bold text-slate-500 tracking-wide">
                        {tip.category}
                      </span>
                    </div>
                    <h3 className="font-black text-slate-900 text-lg leading-tight mb-2 group-hover:text-orange-600 transition-colors">
                      {tip.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                      {tip.introduction}
                    </p>
                    <div className="flex items-center justify-end text-xs text-slate-500 border-t border-slate-100 pt-3">
                      <span className="text-orange-600 font-bold uppercase tracking-wide">
                        {t('common.readMore')} →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
    </div>
  );
};

export default TipsPage;
